import { AnyAction } from 'redux';
import {
  FamilienstandChanged,
  GeburtsdatumChanged,
  HausnummerChanged,
  OrtChanged,
  PlzChanged,
  Produktlinie,
  StrasseChanged,
  VermietetesBebautesModalObjektChangedPayload,
  VermietetesModalObjektChangedPayload,
  VermietetesObjekt,
  VermietetesUnbebautesModalObjektChangedPayload,
  VersicherterBereichEnum,
} from '@ps20-frontend/frontend-models';
import {
  FahrzeugArt,
  FahrzeugeVersichert,
  FamilienStand,
  Produktlinienname,
  Selbstbeteiligung,
} from '@ps20/bff-models';
import { TARIF_CHECKED } from '@ps20-frontend/shop-integration-redux';
import { Injectable } from '@angular/core';
import { OptionaleLeistungChanged } from './models/optinaleLeistungChanged';
import { AntragsummerGenerated } from './action-types/antragsummerGenerated';

export interface RsAction<T = undefined> extends AnyAction {
  payload?: T;
}

@Injectable()
export class RsActions {
  static readonly FAMILIENSTAND_CHANGED = 'FAMILIENSTAND_CHANGED';
  static readonly GEBURTSDATUM_CHANGED = 'GEBURTSDATUM_CHANGED';
  static readonly HAUSNUMMER_CHANGED = 'HAUSNUMMER_CHANGED';
  static readonly ORT_CHANGED = 'ORT_CHANGED';
  static readonly PLZ_CHANGED = 'PLZ_CHANGED';
  static readonly PREISE_REQUESTED = 'PREISE_REQUESTED';
  static readonly STRASSE_CHANGED = 'STRASSE_CHANGED';
  static readonly ZUM_ANGEBOT_BUTTON_CLICKED = 'ZUM_ANGEBOT_BUTTON_CLICKED';
  static readonly ZURUECK_ZUM_SHOP_MODAL_CLOSE = 'ZURUECK_ZUM_SHOP_MODAL_CLOSE';
  static readonly ZURUECK_ZUM_SHOP_MODAL_OPEN = 'ZURUECK_ZUM_SHOP_MODAL_OPEN';
  static readonly SELBSTSTAENDIGKEIT_CHANGED = 'SELBSTSTAENDIGKEIT_CHANGED';
  static readonly GESCHAEFTSFUEHRER_CHANGED = 'GESCHAEFTSFUEHRER_CHANGED';
  static readonly OEFFENTLICHER_DIENST_CHANGED = 'OEFFENTLICHER_DIENST_CHANGED';
  static readonly RECHTSSTREITIGKEITEN_CHANGED = 'RECHTSSTREITIGKEITEN_CHANGED';
  static readonly ANZAHL_RECHTSSTREITIGKEITEN_CHANGED = 'ANZAHL_RECHTSSTREITIGKEITEN_CHANGED';
  static readonly SELBSTBETEILIGUNG_CHANGED = 'SELBSTBETEILIGUNG_CHANGED';
  static readonly ANZAHLFAHRZEUG_CHANGED = 'ANZAHLFAHRZEUG_CHANGED';
  static readonly FAHRZEUGART_CHANGED = 'FAHRZEUGART_CHANGED';
  static readonly VERSICHERTER_BEREICH_CHANGED = 'VERSICHERTER_BEREICH_CHANGED';
  static readonly OPTIONALE_LEISTUNG_CHANGED = 'OPTIONALE_LEISTUNG_CHANGED';
  static readonly PRODUCT_LOADING_IN_PROGRESS = 'PRODUCT_LOADING_IN_PROGRESS';
  static readonly ZUSATZOPTION_CHECKED = 'ZUSATZOPTION_CHECKED';
  static readonly ZUSATZOPTION_UNCHECKED = 'ZUSATZOPTION_UNCHECKED';
  static readonly OVERWRITE_ZUSATZOPTION = 'OVERWRITE_ZUSATZOPTION';
  static readonly VERMIETETE_OBJEKTE_UPDATED = 'VERMIETETE_OBJEKTE_UPDATED';
  static readonly ABSPRUNG_INS_DASHBOARD = 'ABSPRUNG_INS_DASHBOARD';
  static readonly VORSCHLAGDATEN_CHANGED = 'VORSCHLAGDATEN_CHANGED';
  static readonly ANTRAGSNUMMER_GENERATED = 'ANTRAGSNUMMER_GENERATED';
  static readonly PRODUKTLINIEN_CHANGED = 'PRODUKTLINIEN_CHANGED';
  static readonly VERMIETETES_MODAL_OBJEKT_CHANGED = 'VERMIETETES_MODAL_OBJEKT_CHANGED';
  static readonly VERMIETETES_BEBAUTES_MODAL_OBJEKT_CHANGED = 'VERMIETETES_BEBAUTES_MODAL_OBJEKT_CHANGED';
  static readonly VERMIETETES_UNBEBAUTES_MODAL_OBJEKT_CHANGED =
    'VERMIETETES_UNBEBAUTES_MODAL_OBJEKT_CHANGED';
  static readonly VERMIETETES_MODAL_OBJEKT_ADDED = 'VERMIETETES_MODAL_OBJEKT_ADDED';
  static readonly VERMIETETES_MODAL_OBJEKT_DELETED = 'VERMIETETES_MODAL_OBJEKT_DELETED';
  static readonly VERMIETER_MODAL_IS_OPEN_CHANGED = 'VERMIETER_MODAL_IS_OPEN_CHANGED';
  static readonly QUOTEDB_REQUESTED_RS = 'QUOTEDB_REQUESTED_RS';
  static readonly OVERWRITE_VERMIETETE_OBJEKTE = 'OVERWRITE_VERMIETETE_OBJEKTE';
  static readonly AUSLAENDISCHE_ADRESSE_CHANGED = 'AUSLAENDISCHE_ADRESSE_CHANGED';
  static readonly HANDLE_RS_NAVIGATION_TO_ANGEBOT = 'HANDLE_RS_NAVIGATION_TO_ANGEBOT';

  overwriteVermieteteObjekte(
    vermieteteObjekte: Array<VermietetesObjekt>,
  ): RsAction<Array<VermietetesObjekt>> {
    return {
      type: RsActions.OVERWRITE_VERMIETETE_OBJEKTE,
      payload: vermieteteObjekte,
    };
  }

  selbstbeteiligungChanged(selbstbeteiligung: Selbstbeteiligung): RsAction<Selbstbeteiligung> {
    return {
      type: RsActions.SELBSTBETEILIGUNG_CHANGED,
      payload: selbstbeteiligung,
    };
  }

  zurueckZumShopModalOpen(): RsAction {
    return { type: RsActions.ZURUECK_ZUM_SHOP_MODAL_OPEN };
  }

  anzahlFahrzeugChanged(anzahlFahrzeug: FahrzeugeVersichert): RsAction<FahrzeugeVersichert> {
    return { type: RsActions.ANZAHLFAHRZEUG_CHANGED, payload: anzahlFahrzeug };
  }

  fahrzeugartChanged(fahrzeugart: FahrzeugArt): RsAction<FahrzeugArt> {
    return { type: RsActions.FAHRZEUGART_CHANGED, payload: fahrzeugart };
  }

  familienstandChanged(familienstand: FamilienStand): RsAction<FamilienstandChanged> {
    return {
      type: RsActions.FAMILIENSTAND_CHANGED,
      payload: { familienstand },
    };
  }

  geburtsdatumChanged(geburtsdatum: string): RsAction<GeburtsdatumChanged> {
    return { type: RsActions.GEBURTSDATUM_CHANGED, payload: { geburtsdatum } };
  }

  hausnummerChanged(hausnummer: string): RsAction<HausnummerChanged> {
    return { type: RsActions.HAUSNUMMER_CHANGED, payload: { hausnummer } };
  }

  optionaleLeistungChanged(
    produktName: Produktlinienname,
    eigenschaftId: string,
    value: boolean,
  ): RsAction<OptionaleLeistungChanged> {
    return {
      type: RsActions.OPTIONALE_LEISTUNG_CHANGED,
      payload: {
        produktName,
        eigenschaftId,
        value,
      },
    };
  }

  ortChanged(ort: string): RsAction<OrtChanged> {
    return { type: RsActions.ORT_CHANGED, payload: { ort } };
  }

  plzChanged(plz: string): RsAction<PlzChanged> {
    return { type: RsActions.PLZ_CHANGED, payload: { plz } };
  }

  preiseRequested(): RsAction {
    return { type: RsActions.PREISE_REQUESTED };
  }

  selbststaendigkeitChanged(selbststaendig: boolean): RsAction<boolean> {
    return {
      type: RsActions.SELBSTSTAENDIGKEIT_CHANGED,
      payload: selbststaendig,
    };
  }

  versicherterBereichChanged(
    versicherterBereich: VersicherterBereichEnum,
  ): RsAction<VersicherterBereichEnum> {
    return {
      type: RsActions.VERSICHERTER_BEREICH_CHANGED,
      payload: versicherterBereich,
    };
  }

  geschaeftsfuehrerChanged(geschaeftsfuehrer: boolean): RsAction<boolean> {
    return {
      type: RsActions.GESCHAEFTSFUEHRER_CHANGED,
      payload: geschaeftsfuehrer,
    };
  }

  oeffentlicherDienstChanged(imOeffentlichenDienstTaetig: boolean): RsAction<boolean> {
    return {
      type: RsActions.OEFFENTLICHER_DIENST_CHANGED,
      payload: imOeffentlichenDienstTaetig,
    };
  }

  rechtsstreitigkeitenVorhandenChanged(rechtsstreitigkeitenVorhanden: boolean): RsAction<boolean> {
    return {
      type: RsActions.RECHTSSTREITIGKEITEN_CHANGED,
      payload: rechtsstreitigkeitenVorhanden,
    };
  }

  anzahlRechtsstreitigkeitenChanged(anzahlRechtsstreitigkeiten: number): RsAction<number> {
    return {
      type: RsActions.ANZAHL_RECHTSSTREITIGKEITEN_CHANGED,
      payload: anzahlRechtsstreitigkeiten,
    };
  }

  strasseChanged(strasse: string): RsAction<StrasseChanged> {
    return { type: RsActions.STRASSE_CHANGED, payload: { strasse } };
  }

  tarifChecked(index: number): RsAction<{ index: number }> {
    return { type: TARIF_CHECKED, payload: { index } };
  }

  vermieteteObjekteUpdated(vermieteteObjekte: VermietetesObjekt[]): RsAction<VermietetesObjekt[]> {
    return {
      type: RsActions.VERMIETETE_OBJEKTE_UPDATED,
      payload: vermieteteObjekte,
    };
  }

  zumAngebotButtonClicked(): RsAction {
    return { type: RsActions.ZUM_ANGEBOT_BUTTON_CLICKED };
  }

  zurueckZumShopModalClose(): RsAction {
    return { type: RsActions.ZURUECK_ZUM_SHOP_MODAL_CLOSE };
  }

  productLoadingInProgress(isLoading: boolean): RsAction<boolean> {
    return { type: RsActions.PRODUCT_LOADING_IN_PROGRESS, payload: isLoading };
  }

  absprungInsDashboard(): RsAction {
    return { type: RsActions.ABSPRUNG_INS_DASHBOARD };
  }

  vorschlagdatenChanged(): AnyAction {
    return { type: RsActions.VORSCHLAGDATEN_CHANGED };
  }

  antragsnummerGenerated(antragsnummer: string): RsAction<AntragsummerGenerated> {
    return {
      type: RsActions.ANTRAGSNUMMER_GENERATED,
      payload: { antragsnummer },
    };
  }

  produktlinienChanged(produktlinien: Produktlinie[]): RsAction<Produktlinie[]> {
    return { type: RsActions.PRODUKTLINIEN_CHANGED, payload: produktlinien };
  }

  overwriteZusatzoptionen(
    changes: Array<{ name: string; status: boolean }>,
  ): RsAction<Array<{ name: string; status: boolean }>> {
    return { type: RsActions.OVERWRITE_ZUSATZOPTION, payload: changes };
  }

  zusatzoptionChecked(name: string): RsAction<{ name: string }> {
    return { type: RsActions.ZUSATZOPTION_CHECKED, payload: { name } };
  }

  zusatzoptionUnchecked(name: string): RsAction<{ name: string }> {
    return { type: RsActions.ZUSATZOPTION_UNCHECKED, payload: { name } };
  }

  vermietetesModalObjektChanged(
    changedVermietetesObjekt: VermietetesModalObjektChangedPayload,
  ): RsAction<VermietetesModalObjektChangedPayload> {
    return {
      type: RsActions.VERMIETETES_MODAL_OBJEKT_CHANGED,
      payload: changedVermietetesObjekt,
    };
  }

  vermietetesBebautesModalObjektChanged(
    changedVermietetesObjekt: VermietetesBebautesModalObjektChangedPayload,
  ): RsAction<VermietetesBebautesModalObjektChangedPayload> {
    return {
      type: RsActions.VERMIETETES_BEBAUTES_MODAL_OBJEKT_CHANGED,
      payload: changedVermietetesObjekt,
    };
  }

  vermietetesUnbebautesModalObjektChanged(
    changedVermietetesObjekt: VermietetesUnbebautesModalObjektChangedPayload,
  ): RsAction<VermietetesUnbebautesModalObjektChangedPayload> {
    return {
      type: RsActions.VERMIETETES_UNBEBAUTES_MODAL_OBJEKT_CHANGED,
      payload: changedVermietetesObjekt,
    };
  }

  vermietetesModalObjektAdded(): RsAction {
    return { type: RsActions.VERMIETETES_MODAL_OBJEKT_ADDED };
  }

  vermietetesModalObjektDeleted(index: number): RsAction<number> {
    return { type: RsActions.VERMIETETES_MODAL_OBJEKT_DELETED, payload: index };
  }

  vermieterModalIsOpenChanged(isChecked: boolean): RsAction<boolean> {
    return {
      type: RsActions.VERMIETER_MODAL_IS_OPEN_CHANGED,
      payload: isChecked,
    };
  }

  quoteDBRequested(): AnyAction {
    return { type: RsActions.QUOTEDB_REQUESTED_RS };
  }

  auslaendischeAdressChecboxChanged(isCheckboxChecked: boolean): RsAction<boolean> {
    return {
      type: RsActions.AUSLAENDISCHE_ADRESSE_CHANGED,
      payload: isCheckboxChecked,
    };
  }

  handleRsNavigationToAngebot(): RsAction {
    return { type: RsActions.HANDLE_RS_NAVIGATION_TO_ANGEBOT };
  }
}
